import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, ButtonGroup, Checkbox, CheckPicker, DateRangePicker, Divider, Dropdown, Loader, Message, Panel, SelectPicker, Stack } from 'rsuite';

import {
  Chart as ChartJS,
  CategoryScale,
  Decimation,
  Filler,
  Title,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import {
  useProjects,
  useTasks,
  useUsers,
  useCloseDates,
  useProjectForecastedHours,
  useProjectForecastedRevenue,
  useProjectActualRevenue,
  useProjectInvoicedAmount,
  useProjectHoursByUser,
  useProjectChartDateSpan,
  useProjectActualHours,
  useAllProjectsChartsSummary,
  useProjectMilestones,
  useProjectsMilestones,
} from '../hooks/useQueries';

import {
  bucketStartForInterval,
  buildPeriodStarts,
  formatCurrency,
  formatHours,
  formatTooltipTitle,
  formatXAxisTick,
  parseISODate,
  readCssVar,
  toISODate,
} from '../utils/chartUtils';
import { exportSheetsToXlsx } from '../utils/excelExport';

import './ChartsPage.css';

const milestonePlugin = {
  id: 'milestoneLines',
  beforeDatasetsDraw: (chart, _args, pluginOptions) => {
    const milestones = Array.isArray(pluginOptions?.milestones) ? pluginOptions.milestones : [];
    const interval = String(pluginOptions?.interval || 'month');
    if (milestones.length === 0) return;

    const xScale = chart?.scales?.x;
    const area = chart?.chartArea;
    const labels = Array.isArray(chart?.data?.labels) ? chart.data.labels.map(String) : [];
    if (!xScale || !area || labels.length === 0) return;

    const ctx = chart.ctx;
    const stroke = 'rgba(99, 102, 241, 0.75)';
    const milestonesByIndex = new Map();

    for (const milestone of milestones) {
      const d = parseISODate(String(milestone?.milestone_date || '').slice(0, 10));
      if (!d) continue;

      const bucket = bucketStartForInterval(interval, d);
      const bucketIso = toISODate(bucket);
      if (!bucketIso) continue;

      const index = labels.findIndex((l) => l === bucketIso);
      if (index < 0) continue;

      const x = xScale.getPixelForValue(index);
      if (!Number.isFinite(x)) continue;

      const label = String(milestone?.name || 'Milestone').trim() || 'Milestone';
      if (!milestonesByIndex.has(index)) {
        milestonesByIndex.set(index, { x, labels: [] });
      }
      milestonesByIndex.get(index).labels.push(label);
    }

    for (const [, group] of milestonesByIndex.entries()) {
      const x = Number(group?.x);
      if (!Number.isFinite(x)) continue;
      const labelsForDate = Array.isArray(group?.labels) ? group.labels : [];
      if (labelsForDate.length === 0) continue;

      ctx.save();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(x, area.top);
      ctx.lineTo(x, area.bottom);
      ctx.stroke();
      ctx.setLineDash([]);

      const rawLabel = labelsForDate.length > 1
        ? `${labelsForDate.length} milestones`
        : String(labelsForDate[0] || 'Milestone');
      const label = (() => {
        const colonIdx = rawLabel.indexOf(':');
        if (colonIdx <= 0) return rawLabel;
        const projectPart = rawLabel.slice(0, colonIdx).trim();
        const milestonePart = rawLabel.slice(colonIdx + 1).trim();
        const shortProject = projectPart.length > 24 ? `${projectPart.slice(0, 24)}…` : projectPart;
        return milestonePart ? `${shortProject}: ${milestonePart}` : shortProject;
      })();
      ctx.font = '11px sans-serif';
      ctx.textBaseline = 'top';

      const maxLabelTextWidth = 140;
      const maxLabelLines = 2;
      const trimToWidth = (text) => {
        const s0 = String(text || '').trim();
        if (!s0) return '';
        if (ctx.measureText(s0).width <= maxLabelTextWidth) return s0;
        let s = s0;
        while (s.length > 1 && ctx.measureText(`${s}…`).width > maxLabelTextWidth) {
          s = s.slice(0, -1);
        }
        return `${s}…`;
      };
      const words = label.split(/\s+/).filter(Boolean);
      const wrappedLines = [];
      let currentLine = '';
      for (const word of words) {
        if (wrappedLines.length >= maxLabelLines) break;
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(candidate).width <= maxLabelTextWidth) {
          currentLine = candidate;
        } else {
          if (currentLine) wrappedLines.push(trimToWidth(currentLine));
          if (wrappedLines.length >= maxLabelLines) {
            currentLine = '';
            break;
          }
          currentLine = trimToWidth(word);
        }
      }
      if (currentLine && wrappedLines.length < maxLabelLines) wrappedLines.push(trimToWidth(currentLine));

      const lines = (wrappedLines.length > 0 ? wrappedLines : [trimToWidth(label)]).slice(0, maxLabelLines);

      const textWidth = Math.ceil(
        lines.reduce((max, line) => Math.max(max, ctx.measureText(String(line)).width), 0)
      );
      const lineHeight = 12;
      const padX = 6;
      const padY = 3;
      const radius = 7;
      const boxWidth = textWidth + padX * 2;
      const boxHeight = lineHeight * lines.length + padY * 2;
      const rawBoxX = x - boxWidth / 2;
      const minX = area.left + 2;
      const maxX = area.right - boxWidth - 2;
      const boxX = Math.max(minX, Math.min(maxX, rawBoxX));
      const isEdgeClamped = Math.abs(boxX - rawBoxX) > 0.5;
      const edgeOffsetY = isEdgeClamped ? (boxHeight + 6) : 0;
      const boxY = area.top + 2 + edgeOffsetY;

      const r = Math.min(radius, boxHeight / 2, boxWidth / 2);
      ctx.beginPath();
      ctx.moveTo(boxX + r, boxY);
      ctx.lineTo(boxX + boxWidth - r, boxY);
      ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + r);
      ctx.lineTo(boxX + boxWidth, boxY + boxHeight - r);
      ctx.quadraticCurveTo(boxX + boxWidth, boxY + boxHeight, boxX + boxWidth - r, boxY + boxHeight);
      ctx.lineTo(boxX + r, boxY + boxHeight);
      ctx.quadraticCurveTo(boxX, boxY + boxHeight, boxX, boxY + boxHeight - r);
      ctx.lineTo(boxX, boxY + r);
      ctx.quadraticCurveTo(boxX, boxY, boxX + r, boxY);
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
      ctx.fill();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = stroke;
      ctx.textAlign = 'center';
      const textCenterX = boxX + boxWidth / 2;
      for (let i = 0; i < lines.length; i += 1) {
        ctx.fillText(String(lines[i]), textCenterX, boxY + padY + i * lineHeight);
      }
      ctx.restore();
    }
  },
};

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Filler, Tooltip, Legend, Decimation, milestonePlugin);

const CHARTS_PROJECT_STORAGE_KEY = 'ppc.charts.projectId';
const CHARTS_INTERVAL_STORAGE_KEY = 'ppc.charts.interval';
const CHARTS_START_STORAGE_KEY = 'ppc.charts.start';
const CHARTS_END_STORAGE_KEY = 'ppc.charts.end';
const CHARTS_ALL_PROJECTS_BY_USER_SELECTION_KEY = 'ppc.charts.allProjects.byUserSeries';

const safeInt = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
};

const isOptionalNotImplemented = (err) => {
  const msg = String(err?.message || '');
  return /\bHTTP\s*501\b/.test(msg) || /not implemented/i.test(msg);
};

const resolveInterval = (raw) => {
  const v = String(raw || '').toLowerCase();
  return v === 'month' || v === 'quarter' || v === 'year' ? v : 'month';
};

const readStoredAllProjectsByUserSelection = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CHARTS_ALL_PROJECTS_BY_USER_SELECTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const values = parsed.map((v) => String(v || '')).filter((v) => v.length > 0);
    return values.length > 0 ? values : null;
  } catch {
    return null;
  }
};

const intervalOptions = [
  { label: 'Month', value: 'month' },
  { label: 'Quarter', value: 'quarter' },
  { label: 'Year', value: 'year' },
];

const clampPeriods = (periodStarts, maxPeriods = 100) => {
  if (!Array.isArray(periodStarts)) return [];
  if (periodStarts.length <= maxPeriods) return periodStarts;
  return periodStarts.slice(periodStarts.length - maxPeriods);
};

const downloadTextFile = (filename, text, mime = 'text/plain;charset=utf-8') => {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const chartToCsv = (chartData) => {
  const labels = Array.isArray(chartData?.labels) ? chartData.labels : [];
  const datasets = Array.isArray(chartData?.datasets) ? chartData.datasets : [];
  const headers = ['Period', ...datasets.map((d) => String(d?.label ?? ''))];
  const rows = labels.map((label, idx) => {
    const cols = [String(label)];
    for (const ds of datasets) cols.push(String(ds?.data?.[idx] ?? 0));
    return cols;
  });

  const escape = (v) => {
    const s = String(v ?? '');
    if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
    return s;
  };

  return [headers, ...rows].map((r) => r.map(escape).join(',')).join('\n');
};

const coerceSeriesPoints = (raw) => {
  if (Array.isArray(raw)) {
    return raw
      .map((p) => ({
        date: String(p?.date ?? p?.period_start ?? p?.periodStart ?? p?.label ?? ''),
        value: Number(p?.value ?? p?.hours ?? p?.amount ?? 0),
      }))
      .filter((p) => p.date);
  }

  const labels = Array.isArray(raw?.labels) ? raw.labels : [];
  const ds0 = Array.isArray(raw?.datasets) ? raw.datasets[0] : null;
  const data = Array.isArray(ds0?.data) ? ds0.data : [];
  return labels.map((l, idx) => ({ date: String(l), value: Number(data[idx] ?? 0) }));
};

const normalizePeriodLabelToIsoDate10 = (raw) => {
  const s = String(raw ?? '').trim();
  if (!s) return null;

  // Already ISO date.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // YYYY-MM -> YYYY-MM-01
  const mMonth = s.match(/^(\d{4})-(\d{2})$/);
  if (mMonth) return `${mMonth[1]}-${mMonth[2]}-01`;

  // YYYY -> YYYY-01-01
  const mYear = s.match(/^(\d{4})$/);
  if (mYear) return `${mYear[1]}-01-01`;

  // YYYY-Qn -> first month of the quarter
  const mQuarter = s.match(/^(\d{4})-Q([1-4])$/i);
  if (mQuarter) {
    const year = mQuarter[1];
    const q = Number(mQuarter[2]);
    const month = q === 1 ? '01' : q === 2 ? '04' : q === 3 ? '07' : '10';
    return `${year}-${month}-01`;
  }

  return null;
};

// Step 2: aggregateDataByInterval(rawData, interval, rangeStart, rangeEnd)
const aggregateDataByInterval = (rawData, interval, rangeStart, rangeEnd) => {
  const points = coerceSeriesPoints(rawData);
  const starts = clampPeriods(buildPeriodStarts({ interval, rangeStart, rangeEnd }), 100);

  if (!starts.length) {
    return points
      .map((p) => ({
        period_start: normalizePeriodLabelToIsoDate10(p.date) ?? String(p.date ?? ''),
        value: Number(p.value ?? 0),
      }))
      .filter((p) => p.period_start);
  }

  const sums = new Map();
  for (const p of points) {
    const normalized = normalizePeriodLabelToIsoDate10(p.date) ?? String(p.date ?? '');
    const d = parseISODate(normalized);
    if (!d) continue;
    const bucket = bucketStartForInterval(interval, d);
    const iso = toISODate(bucket);
    if (!iso) continue;
    sums.set(iso, Number(sums.get(iso) ?? 0) + Number(p.value ?? 0));
  }

  return starts.map((s) => {
    const iso = toISODate(s);
    return { period_start: iso, value: Number(sums.get(iso) ?? 0) };
  });
};

// Step 2: transformToChartJsFormat(aggregatedData, datasetLabel, chartTheme)
const transformToChartJsFormat = (aggregatedData, datasetLabel, chartTheme) => {
  const rows = Array.isArray(aggregatedData) ? aggregatedData : [];
  const labels = rows.map((r) => String(r?.period_start ?? ''));
  const data = rows.map((r) => Number(r?.value ?? 0));

  return {
    labels,
    datasets: [
      {
        label: datasetLabel,
        data,
        borderColor: chartTheme.primary,
        backgroundColor: chartTheme.primaryFill,
        fill: true,
        tension: 0.1,
        pointRadius: 2,
        pointHoverRadius: 5,
        borderWidth: 2,
      },
    ],
  };
};

const getSeriesColorByIndex = (index, chartTheme) => {
  const idx = Number(index);
  const safeIdx = Number.isInteger(idx) && idx >= 0 ? idx : 0;
  const palette = Array.isArray(chartTheme?.userPalette) ? chartTheme.userPalette.filter(Boolean) : [];

  if (safeIdx < palette.length) return palette[safeIdx];

  const goldenAngle = 137.508;
  const hue = Math.round(((safeIdx - palette.length) * goldenAngle) % 360);
  return `hsl(${hue < 0 ? hue + 360 : hue}, 72%, 44%)`;
};

const getByUserSeriesKey = (dataset, index = 0) => {
  const uid = Number(dataset?.userId);
  if (Number.isInteger(uid)) return `id:${uid}`;
  const label = String(dataset?.label ?? '').trim();
  return label ? `label:${label}::${index}` : `idx:${index}`;
};

// Step 2: processHoursByUserData(rawData, interval, rangeStart, rangeEnd)
const processHoursByUserData = (rawData, interval, rangeStart, rangeEnd, chartTheme) => {
  const labels = clampPeriods(buildPeriodStarts({ interval, rangeStart, rangeEnd }), 100).map((d) => toISODate(d));

  const rawLabels = Array.isArray(rawData?.labels) ? rawData.labels.map(String) : [];
  const rawDatasets = Array.isArray(rawData?.datasets) ? rawData.datasets : [];

  const datasets = rawDatasets.map((ds, idx) => {
    const points = rawLabels.map((l, i) => ({ date: l, value: Number(ds?.data?.[i] ?? 0) }));
    const aggregated = aggregateDataByInterval(points, interval, rangeStart, rangeEnd);
    const byPeriod = new Map(aggregated.map((r) => [String(r.period_start), Number(r.value ?? 0)]));
    const color = getSeriesColorByIndex(idx, chartTheme) || chartTheme.primary;

    return {
      label: String(ds?.label ?? 'User'),
      userId: Number.isInteger(Number(ds?.userId)) ? Number(ds.userId) : null,
      data: labels.map((l) => Number(byPeriod.get(l) ?? 0)),
      borderColor: color,
      backgroundColor: color,
      fill: false,
      tension: 0.1,
      pointRadius: 2,
      pointHoverRadius: 5,
      borderWidth: 2,
    };
  });

  return { labels, datasets };
};

const makeYearRanges = () => {
  const y = new Date().getFullYear();
  const yearRange = (yy) => [new Date(yy, 0, 1), new Date(yy, 11, 31)];
  return [
    { label: `This year (${y})`, value: yearRange(y) },
    { label: `Last year (${y - 1})`, value: yearRange(y - 1) },
    { label: `Next year (${y + 1})`, value: yearRange(y + 1) },
  ];
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const makeThisYearRange = (baseDate = new Date()) => {
  const d = startOfDay(baseDate);
  return [new Date(d.getFullYear(), 0, 1), new Date(d.getFullYear(), 11, 31)];
};

const makeLastYearRange = (baseDate = new Date()) => {
  const d = startOfDay(baseDate);
  const y = d.getFullYear() - 1;
  return [new Date(y, 0, 1), new Date(y, 11, 31)];
};

const makeThisQuarterRange = (baseDate = new Date()) => {
  const d = startOfDay(baseDate);
  const q0 = Math.floor(d.getMonth() / 3) * 3;
  const start = new Date(d.getFullYear(), q0, 1);
  const end = new Date(d.getFullYear(), q0 + 3, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return [start, end];
};

const makeProjectDurationRange = (project) => {
  if (!project) return null;

  const startRaw = project?.startDate ?? project?.start_date ?? project?.start;
  const endRaw = project?.endDate ?? project?.end_date ?? project?.end;

  const start = startRaw ? parseISODate(String(startRaw).slice(0, 10)) : null;
  const end = endRaw ? parseISODate(String(endRaw).slice(0, 10)) : null;

  if (!start && !end) return null;

  const a = start ? startOfDay(start) : new Date(end.getFullYear(), 0, 1);
  const b = end ? startOfDay(end) : startOfDay(new Date());
  return a.getTime() <= b.getTime() ? [a, b] : [b, a];
};

const makeAllProjectsDurationRange = (projects) => {
  const list = Array.isArray(projects) ? projects : [];
  const starts = [];
  const ends = [];

  for (const project of list) {
    const startRaw = project?.startDate ?? project?.start_date ?? project?.start;
    const endRaw = project?.endDate ?? project?.end_date ?? project?.end;
    const start = startRaw ? parseISODate(String(startRaw).slice(0, 10)) : null;
    const end = endRaw ? parseISODate(String(endRaw).slice(0, 10)) : null;
    if (start) starts.push(startOfDay(start));
    if (end) ends.push(startOfDay(end));
  }

  if (starts.length === 0 && ends.length === 0) return null;

  const a = starts.length > 0
    ? starts.reduce((min, d) => (d.getTime() < min.getTime() ? d : min))
    : ends.reduce((min, d) => (d.getTime() < min.getTime() ? d : min));

  const b = ends.length > 0
    ? ends.reduce((max, d) => (d.getTime() > max.getTime() ? d : max))
    : starts.reduce((max, d) => (d.getTime() > max.getTime() ? d : max));

  return a.getTime() <= b.getTime() ? [a, b] : [b, a];
};

const readProjectIdFromSearchParams = (searchParams) => {
  const rawFromUrl = searchParams?.get?.('projectId');
    if (typeof rawFromUrl === 'string' && rawFromUrl.toLowerCase() === 'all') return null; // Treat 'all' as null

  const fromUrl = safeInt(rawFromUrl);
  if (fromUrl !== null) return fromUrl;

  try {
    const stored = window.localStorage.getItem(CHARTS_PROJECT_STORAGE_KEY);
    if (typeof stored === 'string' && stored.toLowerCase() === 'all') return null; // Treat stored 'all' as null
    const fromStorage = safeInt(stored);
    if (fromStorage !== null) return fromStorage;
  } catch {
    // ignore
  }

  return null;
};

const hasExplicitAllProjectsSelection = (searchParams) => {
  const rawFromUrl = searchParams?.get?.('projectId');
  if (typeof rawFromUrl === 'string' && rawFromUrl.toLowerCase() === 'all') return true;

  try {
    const stored = window.localStorage.getItem(CHARTS_PROJECT_STORAGE_KEY);
    return typeof stored === 'string' && stored.toLowerCase() === 'all';
  } catch {
    return false;
  }
};

const readIntervalFromSearchParams = (searchParams) => {
  const fromUrl = searchParams?.get?.('interval');
  if (fromUrl) return resolveInterval(fromUrl);

  try {
    const fromStorage = window.localStorage.getItem(CHARTS_INTERVAL_STORAGE_KEY);
    return resolveInterval(fromStorage);
  } catch {
    return 'month';
  }
};

const readRangeFromSearchParams = (searchParams) => {
  const startFromUrl = searchParams?.get?.('start');
  const endFromUrl = searchParams?.get?.('end');

  const start = String(startFromUrl || '').slice(0, 10);
  const end = String(endFromUrl || '').slice(0, 10);
  const ds = parseISODate(start);
  const de = parseISODate(end);
  if (ds && de) return [ds, de];

  try {
    const storedStart = String(window.localStorage.getItem(CHARTS_START_STORAGE_KEY) || '').slice(0, 10);
    const storedEnd = String(window.localStorage.getItem(CHARTS_END_STORAGE_KEY) || '').slice(0, 10);
    const sds = parseISODate(storedStart);
    const sde = parseISODate(storedEnd);
    if (!sds || !sde) return null;
    return [sds, sde];
  } catch {
    return null;
  }
};

const makeChartTheme = () => {
  const primary = readCssVar('--rs-primary-500', '#1675e0');
  const text = readCssVar('--rs-text-primary', '#111');
  const textMuted = readCssVar('--rs-text-secondary', '#666');
  const grid = readCssVar('--rs-border-primary', 'rgba(0,0,0,0.12)');
  // Tooltips should remain readable regardless of theme variables.
  const tooltipBg = '#fff';
  const tooltipBorder = grid;
  const tooltipText = text;

  const userPalette = [
    primary,
    readCssVar('--rs-blue-500', '#0b5fff'),
    readCssVar('--rs-green-500', '#2ecc71'),
    readCssVar('--rs-orange-500', '#f39c12'),
    readCssVar('--rs-red-500', '#e74c3c'),
    readCssVar('--rs-violet-500', '#8e44ad'),
    readCssVar('--rs-cyan-500', '#00bcd4'),
  ].filter(Boolean);

  const primaryFill = (() => {
    const s = String(primary).trim();
    const hex = s.match(/^#([0-9a-f]{6})$/i);
    if (hex) {
      const n = parseInt(hex[1], 16);
      const r = (n >> 16) & 255;
      const g = (n >> 8) & 255;
      const b = n & 255;
      return `rgba(${r}, ${g}, ${b}, 0.15)`;
    }
    const rgb = s.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
    if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, 0.15)`;
    return 'rgba(22, 117, 224, 0.15)';
  })();

  return { primary, primaryFill, text, textMuted, grid, tooltipBg, tooltipBorder, tooltipText, userPalette };
};

const makeBaseOptions = ({
  interval,
  includeYear,
  theme,
  title,
  yAxisTitle,
  valueFormatter,
  tooltipLabelFormatter,
  milestones,
  onLegendItemClick,
}) => {
  const milestoneNamesByLabel = new Map();
  const milestoneList = Array.isArray(milestones) ? milestones : [];
  for (const milestone of milestoneList) {
    const d = parseISODate(String(milestone?.milestone_date || '').slice(0, 10));
    if (!d) continue;
    const bucket = bucketStartForInterval(interval, d);
    const bucketIso = toISODate(bucket);
    if (!bucketIso) continue;
    const names = milestoneNamesByLabel.get(bucketIso) || [];
    names.push(String(milestone?.name || 'Milestone').trim() || 'Milestone');
    milestoneNamesByLabel.set(bucketIso, names);
  }

  return ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 300 },
  interaction: { mode: 'index', intersect: false, axis: 'x' },
  scales: {
    x: {
      type: 'category',
      grid: { color: theme.grid },
      ticks: {
        display: true,
        color: theme.textMuted,
        callback: function tickCb(value) {
          const raw = this.getLabelForValue(value);
          return formatXAxisTick(raw, interval, includeYear);
        },
        maxRotation: 0,
        autoSkip: true,
      },
      title: {
        display: false,
        text: '',
      },
    },
    y: {
      beginAtZero: true,
      grid: { color: theme.grid },
      ticks: {
        color: theme.textMuted,
        callback: (v) => valueFormatter(Number(v)),
      },
      title: {
        display: Boolean(yAxisTitle),
        text: yAxisTitle,
        color: theme.text,
      },
    },
  },
  plugins: {
    decimation: { enabled: true, algorithm: 'lttb', samples: 100 },
    legend: {
      display: true,
      position: 'top',
      onClick: (_e, legendItem, legend) => {
        if (typeof onLegendItemClick === 'function') {
          onLegendItemClick(legendItem, legend);
          return;
        }
        const datasetIndex = legendItem?.datasetIndex;
        if (!Number.isInteger(datasetIndex)) return;
        const chart = legend?.chart;
        if (!chart) return;
        chart.setDatasetVisibility(datasetIndex, !chart.isDatasetVisible(datasetIndex));
        chart.update();
      },
      labels: { usePointStyle: true, color: theme.text },
    },
    title: {
      display: Boolean(title),
      text: title,
      color: theme.text,
    },
    tooltip: {
      backgroundColor: theme.tooltipBg,
      titleColor: theme.tooltipText,
      bodyColor: theme.tooltipText,
      borderColor: theme.tooltipBorder,
      borderWidth: 1,
      displayColors: true,
      callbacks: {
        title: (items) => {
          const raw = items?.[0]?.label;
          return formatTooltipTitle(raw, interval, includeYear);
        },
        label: (ctx) => tooltipLabelFormatter(ctx),
        afterBody: (items) => {
          const raw = String(items?.[0]?.label || '');
          const names = milestoneNamesByLabel.get(raw) || [];
          if (!Array.isArray(names) || names.length === 0) return [];
          return ['Milestones:', ...names];
        },
      },
    },
    milestoneLines: {
      milestones,
      interval,
    },
  },
  });
};

const nextPeriodStart = (start, key) => {
  const k = String(key || 'month').toLowerCase();
  const stepMonths = k === 'year' ? 12 : k === 'quarter' ? 3 : 1;
  return new Date(start.getFullYear(), start.getMonth() + stepMonths, 1);
};

const countWeekdaysInclusive = (start, endExclusive) => {
  if (!(start instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  if (a.getTime() >= b.getTime()) return 0;

  let count = 0;
  const cur = new Date(a);
  while (cur.getTime() < b.getTime()) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const parseMonthDay = (raw) => {
  const s = String(raw ?? '').slice(0, 10);
  const m = s.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { month: Number(m[1]), day: Number(m[2]) };
};

const countCloseDatesInRange = (start, endExclusive, rows) => {
  if (!(start instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const list = Array.isArray(rows) ? rows : [];

  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  if (a.getTime() >= b.getTime()) return 0;

  const years = new Set();
  for (let y = a.getFullYear(); y <= b.getFullYear(); y += 1) years.add(y);

  let count = 0;
  for (const r of list) {
    const md = parseMonthDay(r?.closeDate ?? r?.close_date);
    if (!md || !Number.isFinite(md.month) || !Number.isFinite(md.day)) continue;

    for (const y of years) {
      const d = new Date(y, md.month - 1, md.day);
      d.setHours(0, 0, 0, 0);
      if (d.getTime() >= a.getTime() && d.getTime() < b.getTime()) {
        count += 1;
      }
    }
  }
  return count;
};

export default function ChartsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const hoursChartRef = useRef(null);
  const byUserChartRef = useRef(null);
  const burndownChartRef = useRef(null);

  const theme = useMemo(() => makeChartTheme(), []);

  const [projectId, setProjectId] = useState(() => readProjectIdFromSearchParams(searchParams));
  const [interval, setIntervalKey] = useState(() => readIntervalFromSearchParams(searchParams));
  const [hoursChartMetric, setHoursChartMetric] = useState('hours');
  const [byUserMetric, setByUserMetric] = useState('utilization');
  const [showMilestonesPrimary, setShowMilestonesPrimary] = useState(true);
  const [showMilestonesBurndown, setShowMilestonesBurndown] = useState(true);
  const [showMilestonesByUser, setShowMilestonesByUser] = useState(false);

  const needsUtilizationData = byUserMetric === 'utilization';

  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError,
  } = useProjects();

  const {
    data: closeDates = [],
    isLoading: closeDatesLoading,
    error: closeDatesError,
  } = useCloseDates({ enabled: needsUtilizationData });

  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
  } = useUsers({ enabled: needsUtilizationData });

  const userFteById = useMemo(() => {
    const map = new Map();
    for (const u of users || []) {
      const id = Number(u?.id);
      if (!Number.isInteger(id)) continue;
      const raw = Number(u?.ftePercent ?? u?.fte_percent ?? 100);
      const fte = Number.isFinite(raw) ? raw : 100;
      map.set(id, fte);
    }
    return map;
  }, [users]);

  const defaultRange = useMemo(() => {
    const r = makeYearRanges();
    return r?.[0]?.value ?? [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)];
  }, []);

  const [pendingRange, setPendingRange] = useState(() => readRangeFromSearchParams(searchParams) ?? defaultRange);
  const [committedRange, setCommittedRange] = useState(pendingRange);
  const [rangePreset, setRangePreset] = useState('custom');

  const urlHasExplicitRange = useMemo(() => {
    const s = String(searchParams?.get?.('start') || '').slice(0, 10);
    const e = String(searchParams?.get?.('end') || '').slice(0, 10);
    return Boolean(parseISODate(s) && parseISODate(e));
  }, [searchParams]);

  const selectedProject = useMemo(() => {
    if (!Number.isInteger(projectId)) return null;
    const arr = Array.isArray(projects) ? projects : [];
    return (
      arr.find((p) => Number(p?.project_id ?? p?.id) === Number(projectId)) ||
      null
    );
  }, [projectId, projects]);

  const selectedProjectDurationRange = useMemo(
    () => makeProjectDurationRange(selectedProject),
    [selectedProject]
  );

  const allProjectsDurationRange = useMemo(
    () => makeAllProjectsDurationRange(projects),
    [projects]
  );

  const selectionKey = Number.isInteger(projectId) ? `p:${projectId}` : 'all';
  const selectionKeyRef = useRef(selectionKey);
  const hasInitializedDefaultRangeRef = useRef(false);
  const allowAllProjectsRef = useRef(hasExplicitAllProjectsSelection(searchParams));

  // Keep state in sync if user navigates via URL.
  useEffect(() => {
    const nextInterval = readIntervalFromSearchParams(searchParams);
    if (nextInterval !== interval) setIntervalKey(nextInterval);

    const nextRange = readRangeFromSearchParams(searchParams);
    if (nextRange) setPendingRange(nextRange);

    const nextProjectId = readProjectIdFromSearchParams(searchParams);
    allowAllProjectsRef.current = hasExplicitAllProjectsSelection(searchParams);
    if (nextProjectId !== projectId) setProjectId(nextProjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Once projects load, ensure we have a valid selected project.
  useEffect(() => {
    if (!projects || projects.length === 0) return;

    const projectIds = projects
      .map((p) => Number(p?.project_id ?? p?.id))
      .filter((n) => Number.isInteger(n));

    if (projectId === null) {
      if (allowAllProjectsRef.current) return;
      if (projectIds.length > 0) {
        setProjectId(projectIds[0]);
      }
      return;
    }

    if (Number.isInteger(projectId) && projectIds.includes(projectId)) return;

    const candidate = readProjectIdFromSearchParams(searchParams);
    if (Number.isInteger(candidate) && projectIds.includes(candidate)) {
      setProjectId(candidate);
      return;
    }

    setProjectId(projectIds[0]);
  }, [projectId, projects, searchParams]);

  // Default date range behavior:
  // - Single project: project duration (startDate..endDate)
  // - All projects: current year
  // - Respect explicit URL range on first load only
  useEffect(() => {
    const selectionChanged = selectionKeyRef.current !== selectionKey;
    const shouldApplyDefault =
      (!hasInitializedDefaultRangeRef.current && !urlHasExplicitRange) ||
      (hasInitializedDefaultRangeRef.current && selectionChanged);

    if (!shouldApplyDefault) {
      selectionKeyRef.current = selectionKey;
      hasInitializedDefaultRangeRef.current = true;
      return;
    }

    const nextRange =
      selectionKey === 'all'
        ? makeThisYearRange()
        : (selectedProjectDurationRange ?? makeThisYearRange());

    setPendingRange(nextRange);
    setCommittedRange(nextRange);
    setRangePreset(selectionKey === 'all' ? 'this-year' : 'project-duration');

    selectionKeyRef.current = selectionKey;
    hasInitializedDefaultRangeRef.current = true;
  }, [selectionKey, selectedProjectDurationRange, urlHasExplicitRange]);

  // Debounce range changes (Step 7)
  useEffect(() => {
    const t = setTimeout(() => setCommittedRange(pendingRange), 250);
    return () => clearTimeout(t);
  }, [pendingRange]);

  const rangeStart = committedRange?.[0] instanceof Date ? committedRange[0] : null;
  const rangeEnd = committedRange?.[1] instanceof Date ? committedRange[1] : null;
  const startIso = rangeStart ? toISODate(rangeStart) : null;
  const endIso = rangeEnd ? toISODate(rangeEnd) : null;

  // Push filters to URL + localStorage.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (Number.isInteger(projectId)) next.set('projectId', String(projectId));
    else next.set('projectId', 'all'); // Set 'projectId' to 'all' instead of deleting

    next.set('interval', resolveInterval(interval));

    if (startIso && endIso) {
      next.set('start', startIso);
      next.set('end', endIso);
    } else {
      next.delete('start');
      next.delete('end');
    }

    const nextStr = next.toString();
    const curStr = String(searchParams || '');
    if (nextStr !== curStr) setSearchParams(next, { replace: true });

    try {
      if (Number.isInteger(projectId)) window.localStorage.setItem(CHARTS_PROJECT_STORAGE_KEY, String(projectId));
      else window.localStorage.setItem(CHARTS_PROJECT_STORAGE_KEY, 'all');

      window.localStorage.setItem(CHARTS_INTERVAL_STORAGE_KEY, resolveInterval(interval));
      if (startIso) window.localStorage.setItem(CHARTS_START_STORAGE_KEY, startIso);
      if (endIso) window.localStorage.setItem(CHARTS_END_STORAGE_KEY, endIso);
    } catch {
      // ignore
    }
  }, [interval, projectId, startIso, endIso, searchParams, setSearchParams]);

  const includeYear = useMemo(() => {
    if (typeof window === 'undefined') return true;
    const small = window.matchMedia?.('(max-width: 640px)')?.matches;
    return !small;
  }, []);

  const rangePresetOptions = useMemo(() => {
    const hasProjectDuration = Boolean(selectedProjectDurationRange || allProjectsDurationRange);
    return [
      { label: 'This quarter', value: 'this-quarter' },
      { label: 'This year', value: 'this-year' },
      { label: 'Last year', value: 'last-year' },
      { label: 'Project duration', value: 'project-duration', disabled: !hasProjectDuration },
      { label: 'Custom range', value: 'custom' },
    ];
  }, [selectedProjectDurationRange, allProjectsDurationRange]);

  const projectOptions = useMemo(
    () =>
      (projects || [])
        .map((p) => ({
          label: String(p?.project_name ?? p?.name ?? `Project ${p?.project_id ?? p?.id}`),
          value: Number(p?.project_id ?? p?.id),
        }))
        .filter((p) => Number.isInteger(p.value))
        .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [projects]
  );

  const allProjectIds = useMemo(
    () =>
      (projects || [])
        .map((p) => Number(p?.project_id ?? p?.id))
        .filter((n) => Number.isInteger(n)),
    [projects]
  );

  const chartQueryOptions = useMemo(
    () => ({
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }),
    []
  );

  const isAllProjects = !Number.isInteger(projectId);

  const queryEnabled = Boolean((Number.isInteger(projectId) || allProjectIds.length > 0) && startIso && endIso);

  const hoursQuery = useProjectForecastedHours(projectId, interval, startIso, endIso, {
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const actualHoursQuery = useProjectActualHours(projectId, interval, startIso, endIso, {
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const revenueQuery = useProjectForecastedRevenue(projectId, interval, startIso, endIso, {
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const actualRevenueQuery = useProjectActualRevenue(projectId, interval, startIso, endIso, {
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const invoicedAmountQuery = useProjectInvoicedAmount(projectId, interval, startIso, endIso, {
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const byUserQuery = useProjectHoursByUser(projectId, interval, startIso, endIso, {
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });
  const milestonesQuery = useProjectMilestones(projectId, {
    enabled: !isAllProjects && Number.isInteger(projectId),
    ...chartQueryOptions,
  });
  const projectDateSpanQuery = useProjectChartDateSpan(projectId, {
    enabled: !isAllProjects && Number.isInteger(projectId),
    ...chartQueryOptions,
  });

  const tasksQuery = useTasks(projectId, {
    enabled: queryEnabled && !isAllProjects,
    ...chartQueryOptions,
  });

  const allChartsSummaryQuery = useAllProjectsChartsSummary(interval, startIso, endIso, {
    enabled: queryEnabled && isAllProjects,
    ...chartQueryOptions,
  });

  const allMilestonesQuery = useProjectsMilestones({ enabled: isAllProjects, ...chartQueryOptions });

  const totalBudget = useMemo(() => {
    if (isAllProjects) {
      return Number(allChartsSummaryQuery.data?.totalBudget ?? 0);
    }

    return (tasksQuery.data || []).reduce((sum, task) => {
      const budget = Number(task?.budgetTarget ?? task?.budget_target ?? 0);
      return sum + budget;
    }, 0);
  }, [isAllProjects, allChartsSummaryQuery.data?.totalBudget, tasksQuery.data]);

  const allIsLoading =
    allChartsSummaryQuery.isLoading ||
    allMilestonesQuery.isLoading;

  const allError =
    allChartsSummaryQuery.error ||
    allMilestonesQuery.error ||
    null;

  const inferredProjectDurationRange = useMemo(() => {
    const s = parseISODate(String(projectDateSpanQuery.data?.startDate || '').slice(0, 10));
    const e = parseISODate(String(projectDateSpanQuery.data?.endDate || '').slice(0, 10));
    if (!s && !e) return null;
    const a = s ? startOfDay(s) : startOfDay(e);
    const b = e ? startOfDay(e) : startOfDay(s);
    return a.getTime() <= b.getTime() ? [a, b] : [b, a];
  }, [projectDateSpanQuery.data?.endDate, projectDateSpanQuery.data?.startDate]);

  useEffect(() => {
    if (selectionKey === 'all') return;
    if (rangePreset !== 'project-duration') return;
    if (makeProjectDurationRange(selectedProject)) return;
    if (!inferredProjectDurationRange) return;

    const curStart = pendingRange?.[0] instanceof Date ? toISODate(pendingRange[0]) : null;
    const curEnd = pendingRange?.[1] instanceof Date ? toISODate(pendingRange[1]) : null;
    const nextStart = toISODate(inferredProjectDurationRange[0]);
    const nextEnd = toISODate(inferredProjectDurationRange[1]);
    if (curStart === nextStart && curEnd === nextEnd) return;

    setPendingRange(inferredProjectDurationRange);
    setCommittedRange(inferredProjectDurationRange);
  }, [selectionKey, rangePreset, selectedProject, inferredProjectDurationRange, pendingRange]);

  useEffect(() => {
    if (selectionKey !== 'all') return;
    if (rangePreset !== 'project-duration') return;
    if (!allProjectsDurationRange) return;

    const curStart = pendingRange?.[0] instanceof Date ? toISODate(pendingRange[0]) : null;
    const curEnd = pendingRange?.[1] instanceof Date ? toISODate(pendingRange[1]) : null;
    const nextStart = toISODate(allProjectsDurationRange[0]);
    const nextEnd = toISODate(allProjectsDurationRange[1]);
    if (curStart === nextStart && curEnd === nextEnd) return;

    setPendingRange(allProjectsDurationRange);
    setCommittedRange(allProjectsDurationRange);
  }, [selectionKey, rangePreset, allProjectsDurationRange, pendingRange]);

  const optionalActualHoursError = actualHoursQuery.error && !isOptionalNotImplemented(actualHoursQuery.error) ? actualHoursQuery.error : null;
  const optionalActualRevenueError =
    actualRevenueQuery.error && !isOptionalNotImplemented(actualRevenueQuery.error) ? actualRevenueQuery.error : null;
  const optionalInvoicedAmountError =
    invoicedAmountQuery.error && !isOptionalNotImplemented(invoicedAmountQuery.error) ? invoicedAmountQuery.error : null;

  const isLoading =
    projectsLoading ||
    (needsUtilizationData && (closeDatesLoading || usersLoading)) ||
    (isAllProjects
      ? allIsLoading
      : hoursQuery.isLoading || actualHoursQuery.isLoading || revenueQuery.isLoading || invoicedAmountQuery.isLoading || byUserQuery.isLoading);
  const hasError =
    projectsError ||
    (isAllProjects
      ? allError
      : hoursQuery.error || optionalActualHoursError || revenueQuery.error || optionalActualRevenueError || optionalInvoicedAmountError || byUserQuery.error);
  const utilError = needsUtilizationData ? closeDatesError : null;
  const fteError = needsUtilizationData ? usersError : null;

  const workableHoursForPeriod = useCallback(
    (start, endExclusive) => {
      const weekdays = countWeekdaysInclusive(start, endExclusive);
      const closeCount = countCloseDatesInRange(start, endExclusive, closeDates);
      const hours = weekdays * 8 - closeCount * 8;
      return Math.max(0, hours);
    },
    [closeDates]
  );

  const hoursData = useMemo(() => {
    const rawForecast = isAllProjects ? allChartsSummaryQuery.data?.forecastedHours : hoursQuery.data;
    const aggregatedForecast = aggregateDataByInterval(rawForecast, interval, rangeStart, rangeEnd);
    const forecastChart = transformToChartJsFormat(aggregatedForecast, 'Forecasted Hours', theme);
    const forecastHoursColor = '#1675e0';
    const forecastDataset = {
      ...(forecastChart?.datasets?.[0] || {}),
      fill: false,
      borderColor: forecastHoursColor,
      backgroundColor: forecastHoursColor,
      pointBackgroundColor: forecastHoursColor,
      pointBorderColor: forecastHoursColor,
      pointStyle: 'circle',
    };

    const rawActual = isAllProjects
      ? allChartsSummaryQuery.data?.actualHours
      : (!actualHoursQuery.error || !isOptionalNotImplemented(actualHoursQuery.error) ? actualHoursQuery.data : null);

    if (!rawActual) {
      return {
        labels: forecastChart.labels,
        datasets: [forecastDataset],
      };
    }

    const aggregatedActual = aggregateDataByInterval(rawActual, interval, rangeStart, rangeEnd);
    const actualChart = transformToChartJsFormat(aggregatedActual, 'Actual Hours', theme);
    const actualDataset = actualChart?.datasets?.[0];
    const rawActualValues = Array.isArray(actualDataset?.data) ? actualDataset.data : [];

    const firstNonZeroIdx = rawActualValues.findIndex((v) => Number(v) !== 0);
    let lastNonZeroIdx = -1;
    for (let i = rawActualValues.length - 1; i >= 0; i -= 1) {
      if (Number(rawActualValues[i]) !== 0) {
        lastNonZeroIdx = i;
        break;
      }
    }

    if (!actualDataset || firstNonZeroIdx < 0 || lastNonZeroIdx < 0) {
      return {
        labels: forecastChart.labels,
        datasets: [forecastDataset],
      };
    }

    const trimmedActualValues = rawActualValues.map((v, idx) => {
      if (idx < firstNonZeroIdx || idx > lastNonZeroIdx) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    });

    const hasRenderableActual = trimmedActualValues.some((v) => Number.isFinite(Number(v)) && Number(v) !== 0);
    if (!hasRenderableActual) {
      return {
        labels: forecastChart.labels,
        datasets: [forecastDataset],
      };
    }

    return {
      labels: forecastChart.labels,
      datasets: [
        forecastDataset,
        {
          ...actualDataset,
          data: trimmedActualValues,
          borderColor: theme.success || '#4caf50',
          backgroundColor: 'rgba(76, 175, 80, 0.28)',
          fill: {
            target: 0,
            above: 'rgba(76, 175, 80, 0.28)',
            below: 'rgba(76, 175, 80, 0.28)',
          },
          pointBackgroundColor: theme.success || '#4caf50',
          spanGaps: false,
        },
      ],
    };
  }, [
    actualHoursQuery.data,
    actualHoursQuery.error,
    allChartsSummaryQuery.data?.actualHours,
    allChartsSummaryQuery.data?.forecastedHours,
    hoursQuery.data,
    interval,
    isAllProjects,
    rangeEnd,
    rangeStart,
    theme,
  ]);

  const revenueData = useMemo(() => {
    const raw = isAllProjects ? allChartsSummaryQuery.data?.forecastedRevenue : revenueQuery.data;
    const aggregated = aggregateDataByInterval(raw, interval, rangeStart, rangeEnd);
    return transformToChartJsFormat(aggregated, 'Forecasted Revenue', theme);
  }, [allChartsSummaryQuery.data?.forecastedRevenue, interval, isAllProjects, rangeStart, rangeEnd, revenueQuery.data, theme]);

  const actualRevenueData = useMemo(() => {
    const raw = isAllProjects
      ? allChartsSummaryQuery.data?.actualRevenue
      : (!actualRevenueQuery.error || !isOptionalNotImplemented(actualRevenueQuery.error) ? actualRevenueQuery.data : null);

    if (!raw) return null;

    const aggregated = aggregateDataByInterval(raw, interval, rangeStart, rangeEnd);
    return transformToChartJsFormat(aggregated, 'Actual Dollars', theme);
  }, [allChartsSummaryQuery.data?.actualRevenue, actualRevenueQuery.data, actualRevenueQuery.error, interval, isAllProjects, rangeStart, rangeEnd, theme]);

  const invoicedAmountData = useMemo(() => {
    const raw = isAllProjects
      ? allChartsSummaryQuery.data?.invoicedAmount
      : (!invoicedAmountQuery.error || !isOptionalNotImplemented(invoicedAmountQuery.error) ? invoicedAmountQuery.data : null);

    if (!raw) return null;

    const aggregated = aggregateDataByInterval(raw, interval, rangeStart, rangeEnd);
    return transformToChartJsFormat(aggregated, 'Invoiced Amount', theme);
  }, [allChartsSummaryQuery.data?.invoicedAmount, interval, invoicedAmountQuery.data, invoicedAmountQuery.error, isAllProjects, rangeEnd, rangeStart, theme]);

  const dollarsData = useMemo(() => {
    const labels = Array.isArray(revenueData?.labels) ? revenueData.labels : [];
    const forecastValues = Array.isArray(revenueData?.datasets?.[0]?.data)
      ? revenueData.datasets[0].data.map((v) => Number(v ?? 0))
      : [];

    const forecastDataset = {
      ...(revenueData?.datasets?.[0] || {}),
      label: 'Forecasted Dollars',
      data: forecastValues,
      fill: false,
      borderColor: '#1675e0',
      backgroundColor: '#1675e0',
      pointBackgroundColor: '#1675e0',
      pointBorderColor: '#1675e0',
    };

    if (!actualRevenueData?.datasets?.[0]) {
      return { labels, datasets: [forecastDataset] };
    }

    const actualByLabel = new Map(
      (actualRevenueData.labels || []).map((label, idx) => [String(label), Number(actualRevenueData.datasets[0].data?.[idx] ?? 0)])
    );

    const rawActualValues = labels.map((label) => Number(actualByLabel.get(String(label)) ?? 0));
    const firstNonZeroIdx = rawActualValues.findIndex((v) => Number(v) !== 0);
    let lastNonZeroIdx = -1;
    for (let i = rawActualValues.length - 1; i >= 0; i -= 1) {
      if (Number(rawActualValues[i]) !== 0) {
        lastNonZeroIdx = i;
        break;
      }
    }

    if (firstNonZeroIdx < 0 || lastNonZeroIdx < 0) {
      return { labels, datasets: [forecastDataset] };
    }

    const trimmedActualValues = rawActualValues.map((v, idx) => {
      if (idx < firstNonZeroIdx || idx > lastNonZeroIdx) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    });

    const actualDataset = {
      ...actualRevenueData.datasets[0],
      label: 'Actual Dollars',
      data: trimmedActualValues,
      borderColor: theme.success || '#4caf50',
      backgroundColor: 'rgba(76, 175, 80, 0.28)',
      fill: {
        target: '-1',
        above: 'rgba(76, 175, 80, 0.28)',
        below: 'rgba(76, 175, 80, 0.28)',
      },
      pointBackgroundColor: theme.success || '#4caf50',
      spanGaps: false,
    };

    return {
      labels,
      datasets: [forecastDataset, actualDataset],
    };
  }, [actualRevenueData, revenueData, theme]);

  const primaryChartData = hoursChartMetric === 'dollars' ? dollarsData : hoursData;

  const burndownData = useMemo(() => {
    const labels = Array.isArray(revenueData?.labels) ? revenueData.labels : [];
    const revenueValues = Array.isArray(revenueData?.datasets?.[0]?.data)
      ? revenueData.datasets[0].data.map((v) => Number(v ?? 0))
      : [];

    const cumulativeRevenue = [];
    let runningTotal = 0;
    for (const value of revenueValues) {
      runningTotal += value;
      cumulativeRevenue.push(runningTotal);
    }

    const hasInvoicedDataset = Boolean(invoicedAmountData?.datasets?.[0]);
    const invoicedByLabel = new Map(
      (invoicedAmountData?.labels || []).map((label, idx) => [
        String(label),
        Number(invoicedAmountData?.datasets?.[0]?.data?.[idx] ?? 0),
      ])
    );
    const invoicedValues = labels.map((label) => Number(invoicedByLabel.get(String(label)) ?? 0));

    let cumulativeInvoiced = null;
    if (hasInvoicedDataset) {
      cumulativeInvoiced = [];
      let runningInvoiced = 0;
      for (const value of invoicedValues) {
        runningInvoiced += value;
        cumulativeInvoiced.push(runningInvoiced);
      }
    }

    const lastInvoicedIdx = (() => {
      for (let i = invoicedValues.length - 1; i >= 0; i -= 1) {
        if (Number(invoicedValues[i] ?? 0) !== 0) return i;
      }
      return -1;
    })();

    const solidRemainingBudget = labels.map((_, idx) => {
      if (!hasInvoicedDataset || !Array.isArray(cumulativeInvoiced) || idx > lastInvoicedIdx) return null;
      return Number(totalBudget) - Number(cumulativeInvoiced[idx] ?? 0);
    });

    const projectedRemainingBudget = labels.map((_, idx) => {
      if (lastInvoicedIdx < 0) {
        return Number(totalBudget) - Number(cumulativeRevenue[idx] ?? 0);
      }
      if (idx <= lastInvoicedIdx) return null;

      const invoicedBaseline = Number(cumulativeInvoiced?.[lastInvoicedIdx] ?? 0);
      const projectedDelta = Number(cumulativeRevenue[idx] ?? 0) - Number(cumulativeRevenue[lastInvoicedIdx] ?? 0);
      return Number(totalBudget) - (invoicedBaseline + projectedDelta);
    });

    if (lastInvoicedIdx >= 0 && lastInvoicedIdx < labels.length) {
      projectedRemainingBudget[lastInvoicedIdx] = Number(totalBudget) - Number(cumulativeInvoiced?.[lastInvoicedIdx] ?? 0);
    }

    const budgetLine = labels.map(() => totalBudget);

    const datasets = [
        {
          label: 'Budget Target',
          data: budgetLine,
          borderColor: theme.danger || '#f44336',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          tension: 0,
        },
        {
          label: 'Budget Remaining',
          data: solidRemainingBudget,
          borderColor: theme.warning || '#f0ad4e',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.1,
        },
        {
          label: 'Budget Remaining (Projected)',
          data: projectedRemainingBudget,
          borderColor: theme.warning || '#f0ad4e',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 0,
          spanGaps: true,
          tension: 0.1,
        },
        {
          label: 'Projected Revenue',
          data: cumulativeRevenue,
          borderColor: theme.primary || '#3498ff',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 3,
          pointBackgroundColor: theme.primary || '#3498ff',
          tension: 0.1,
        },
      ];

    if (hasInvoicedDataset && Array.isArray(cumulativeInvoiced)) {
      datasets.push({
          label: 'Invoiced Amount',
          data: cumulativeInvoiced,
          borderColor: '#7c3aed',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 3,
          pointBackgroundColor: '#7c3aed',
          tension: 0.1,
      });
    }

    return {
      labels,
      datasets,
    };
  }, [invoicedAmountData, revenueData, totalBudget, theme]);

  const byUserData = useMemo(() => {
    const raw = isAllProjects ? allChartsSummaryQuery.data?.hoursByUser : byUserQuery.data;
    return processHoursByUserData(raw, interval, rangeStart, rangeEnd, theme);
  }, [allChartsSummaryQuery.data?.hoursByUser, byUserQuery.data, interval, isAllProjects, rangeStart, rangeEnd, theme]);

  const byUserUtilizationData = useMemo(() => {
    const labels = Array.isArray(byUserData?.labels) ? byUserData.labels.map(String) : [];
    const labelDates = labels.map((l) => parseISODate(l)).filter(Boolean);

    const periodWorkable = new Map();
    for (let i = 0; i < labels.length; i += 1) {
      const start = parseISODate(labels[i]);
      if (!start) continue;

      const end = (() => {
        const next = labels[i + 1] ? parseISODate(labels[i + 1]) : null;
        if (next) return next;
        return nextPeriodStart(start, interval);
      })();

      periodWorkable.set(labels[i], workableHoursForPeriod(start, end));
    }

    const datasets = (byUserData?.datasets || []).map((ds) => {
      const dsUserId = Number(ds?.userId);
      const ftePercent = Number.isInteger(dsUserId) ? userFteById.get(dsUserId) ?? 100 : 100;
      const fteScale = Math.max(0, Number(ftePercent) / 100);

      const hoursArr = Array.isArray(ds?.data) ? ds.data : [];
      const pctArr = labels.map((label, idx) => {
        const workable = Number(periodWorkable.get(label) ?? 0) * fteScale;
        const hours = Number(hoursArr[idx] ?? 0);
        if (!Number.isFinite(workable) || workable <= 0) return 0;
        if (!Number.isFinite(hours) || hours <= 0) return 0;
        return (hours / workable) * 100;
      });
      return { ...ds, data: pctArr };
    });

    return { labels, datasets, _labelDates: labelDates };
  }, [byUserData, interval, workableHoursForPeriod, userFteById]);

  const chartMilestones = useMemo(() => {
    const rawMilestones = isAllProjects
      ? (Array.isArray(allMilestonesQuery.data) ? allMilestonesQuery.data : [])
      : (Array.isArray(milestonesQuery.data) ? milestonesQuery.data : []);

    const projectNameById = new Map(
      (projects || [])
        .map((p) => {
          const id = Number(p?.project_id ?? p?.id);
          const label = String(p?.project_name ?? p?.name ?? '').trim();
          return [id, label];
        })
        .filter(([id, label]) => Number.isInteger(id) && label)
    );

    return rawMilestones
      .map((milestone) => {
      const projectKey = Number(milestone?.project_id ?? milestone?.projectId);
      const projectLabel = projectNameById.get(projectKey) || null;
      const baseName = String(milestone?.name || 'Milestone').trim() || 'Milestone';
      const displayName = isAllProjects && projectLabel ? `${projectLabel}: ${baseName}` : baseName;

        return {
          ...milestone,
          name: displayName,
        };
      })
      .sort((a, b) => {
        const ad = String(a?.milestone_date || '');
        const bd = String(b?.milestone_date || '');
        if (ad !== bd) return ad.localeCompare(bd);
        return String(a?.name || '').localeCompare(String(b?.name || ''));
      });
  }, [allMilestonesQuery.data, isAllProjects, milestonesQuery.data, projects]);

  const visiblePrimaryMilestones = useMemo(
    () => (showMilestonesPrimary ? chartMilestones : []),
    [showMilestonesPrimary, chartMilestones]
  );
  const visibleBurndownMilestones = useMemo(
    () => (showMilestonesBurndown ? chartMilestones : []),
    [showMilestonesBurndown, chartMilestones]
  );
  const visibleByUserMilestones = useMemo(
    () => (showMilestonesByUser ? chartMilestones : []),
    [showMilestonesByUser, chartMilestones]
  );

  const activeByUserData = byUserMetric === 'utilization' ? byUserUtilizationData : byUserData;
  const byUserSeriesOptions = useMemo(() => {
    const datasets = Array.isArray(activeByUserData?.datasets) ? activeByUserData.datasets : [];
    return datasets.map((ds, idx) => ({
      label: String(ds?.label ?? `User ${idx + 1}`),
      value: getByUserSeriesKey(ds, idx),
    }));
  }, [activeByUserData]);

  const byUserSeriesValues = useMemo(() => byUserSeriesOptions.map((o) => o.value), [byUserSeriesOptions]);

  const byUserSeriesLabelByValue = useMemo(() => {
    const map = new Map();
    for (const opt of byUserSeriesOptions) {
      map.set(opt.value, opt.label);
    }
    return map;
  }, [byUserSeriesOptions]);

  const [selectedByUserSeriesKeys, setSelectedByUserSeriesKeys] = useState(null);

  useEffect(() => {
    if (isAllProjects) {
      const stored = readStoredAllProjectsByUserSelection();
      setSelectedByUserSeriesKeys(stored ?? null);
      return;
    }

    setSelectedByUserSeriesKeys(null);
  }, [isAllProjects, projectId]);

  useEffect(() => {
    if (!isAllProjects || !Array.isArray(selectedByUserSeriesKeys)) return;
    try {
      window.localStorage.setItem(CHARTS_ALL_PROJECTS_BY_USER_SELECTION_KEY, JSON.stringify(selectedByUserSeriesKeys));
    } catch {
      // ignore
    }
  }, [isAllProjects, selectedByUserSeriesKeys]);

  useEffect(() => {
    const allValues = byUserSeriesValues;
    setSelectedByUserSeriesKeys((prev) => {
      if (allValues.length === 0) return prev;

      const next = (() => {
        if (prev === null) return allValues;
        const prevSet = new Set(Array.isArray(prev) ? prev : []);
        const filtered = allValues.filter((v) => prevSet.has(v));
        if (prevSet.size > 0 && filtered.length === 0) return allValues;
        return filtered;
      })();

      const prevList = Array.isArray(prev) ? prev : [];
      if (next.length === prevList.length && next.every((v, i) => v === prevList[i])) {
        return prev;
      }
      return next;
    });
  }, [byUserSeriesValues]);

  const selectedByUserSeriesSet = useMemo(() => {
    const list = Array.isArray(selectedByUserSeriesKeys)
      ? selectedByUserSeriesKeys
      : byUserSeriesValues;
    return new Set(list);
  }, [selectedByUserSeriesKeys, byUserSeriesValues]);

  const activeByUserDataWithVisibility = useMemo(() => {
    const labels = Array.isArray(activeByUserData?.labels) ? activeByUserData.labels : [];
    const datasets = Array.isArray(activeByUserData?.datasets) ? activeByUserData.datasets : [];
    const selectedSet = selectedByUserSeriesSet;

    return {
      ...(activeByUserData || {}),
      labels,
      datasets: datasets.map((ds, idx) => {
        const key = getByUserSeriesKey(ds, idx);
        return {
          ...ds,
          hidden: !selectedSet.has(key),
        };
      }),
    };
  }, [activeByUserData, selectedByUserSeriesSet]);

  const selectAllByUserSeries = useCallback(() => {
    setSelectedByUserSeriesKeys(byUserSeriesOptions.map((o) => o.value));
  }, [byUserSeriesOptions]);

  const deselectAllByUserSeries = useCallback(() => {
    setSelectedByUserSeriesKeys([]);
  }, []);

  const renderByUserSelectionValue = useCallback(
    (value) => {
      const selectedValues = Array.isArray(value) ? value : [];
      const labels = selectedValues
        .map((v) => byUserSeriesLabelByValue.get(v))
        .filter((name) => typeof name === 'string' && name.trim().length > 0);

      if (labels.length === 0) return 'No people selected';
      if (labels.length <= 3) return labels.join(', ');

      const firstThree = labels.slice(0, 3).join(', ');
      return `${firstThree} … ${labels.length - 3} more`;
    },
    [byUserSeriesLabelByValue]
  );

  const onByUserLegendItemClick = useCallback(
    (legendItem, legend) => {
      const datasetIndex = Number(legendItem?.datasetIndex);
      const chart = legend?.chart;
      const datasets = Array.isArray(chart?.data?.datasets) ? chart.data.datasets : [];
      if (!Number.isInteger(datasetIndex) || datasetIndex < 0 || datasetIndex >= datasets.length) return;

      const key = getByUserSeriesKey(datasets[datasetIndex], datasetIndex);
      const allValues = byUserSeriesOptions.map((o) => o.value);

      setSelectedByUserSeriesKeys((prev) => {
        const current = prev === null ? allValues : Array.isArray(prev) ? prev : [];
        const set = new Set(current);
        if (set.has(key)) set.delete(key);
        else set.add(key);
        return allValues.filter((v) => set.has(v));
      });
    },
    [byUserSeriesOptions]
  );

  const hoursOptions = useMemo(
    () =>
      makeBaseOptions({
        interval,
        includeYear,
        theme,
        title: '',
        yAxisTitle: 'Hours',
        valueFormatter: (v) => String(Math.round(Number(v) || 0)),
        tooltipLabelFormatter: (ctx) => `${ctx.dataset?.label ?? 'Hours'}: ${formatHours(ctx.parsed?.y)}`,
        milestones: visiblePrimaryMilestones,
      }),
    [interval, includeYear, theme, visiblePrimaryMilestones]
  );

  const dollarsOptions = useMemo(
    () =>
      makeBaseOptions({
        interval,
        includeYear,
        theme,
        title: '',
        yAxisTitle: 'Dollars (USD)',
        valueFormatter: (v) => formatCurrency(v),
        tooltipLabelFormatter: (ctx) => `${ctx.dataset?.label ?? 'Dollars'}: ${formatCurrency(ctx.parsed?.y)}`,
        milestones: visiblePrimaryMilestones,
      }),
    [interval, includeYear, theme, visiblePrimaryMilestones]
  );

  const burndownOptions = useMemo(
    () => {
      const options = makeBaseOptions({
        interval,
        includeYear,
        theme,
        title: '',
        yAxisTitle: 'Amount (USD)',
        valueFormatter: (v) => formatCurrency(v),
        tooltipLabelFormatter: (ctx) => `${ctx.dataset?.label ?? 'Amount'}: ${formatCurrency(ctx.parsed?.y)}`,
        milestones: visibleBurndownMilestones,
      });

      const existingFilter = options?.plugins?.legend?.labels?.filter;
      if (options?.plugins?.legend?.labels) {
        options.plugins.legend.labels.filter = (legendItem, data) => {
          if (String(legendItem?.text || '') === 'Budget Remaining (Projected)') return false;
          if (typeof existingFilter === 'function') return existingFilter(legendItem, data);
          return true;
        };
      }

      return options;
    },
    [interval, includeYear, theme, visibleBurndownMilestones]
  );

  const byUserOptions = useMemo(() => {
    if (byUserMetric === 'utilization') {
      return makeBaseOptions({
        interval,
        includeYear,
        theme,
        title: '',
        yAxisTitle: '% Utilization',
        valueFormatter: (v) => `${Math.round(Number(v) || 0)}%`,
        tooltipLabelFormatter: (ctx) => `${ctx.dataset?.label ?? 'User'}: ${Math.round(Number(ctx.parsed?.y) || 0)}%`,
        milestones: visibleByUserMilestones,
        onLegendItemClick: onByUserLegendItemClick,
      });
    }

    return makeBaseOptions({
      interval,
      includeYear,
      theme,
      title: '',
      yAxisTitle: 'Hours',
      valueFormatter: (v) => String(Math.round(Number(v) || 0)),
      tooltipLabelFormatter: (ctx) => `${ctx.dataset?.label ?? 'User'}: ${formatHours(ctx.parsed?.y)}`,
      milestones: visibleByUserMilestones,
      onLegendItemClick: onByUserLegendItemClick,
    });
  }, [interval, includeYear, theme, byUserMetric, visibleByUserMilestones, onByUserLegendItemClick]);

  const totalsAndDuration = useMemo(() => {
    const hoursLabels = Array.isArray(hoursData?.labels) ? hoursData.labels.map(String) : [];
    const hoursValues = Array.isArray(hoursData?.datasets?.[0]?.data)
      ? hoursData.datasets[0].data.map((v) => Number(v ?? 0))
      : [];

    const totalHours = hoursValues.reduce((a, b) => a + b, 0);

    const invoicedValues = Array.isArray(invoicedAmountData?.datasets?.[0]?.data)
      ? invoicedAmountData.datasets[0].data.map((v) => Number(v ?? 0))
      : [];
    const totalInvoicedAmount = invoicedValues.reduce((a, b) => a + b, 0);
    const remainingBudget = Number(totalBudget) - Number(totalInvoicedAmount);

    const firstIdx = hoursValues.findIndex((v) => Number(v) !== 0);
    const lastIdx = (() => {
      for (let i = hoursValues.length - 1; i >= 0; i -= 1) {
        if (Number(hoursValues[i]) !== 0) return i;
      }
      return -1;
    })();

    const durationStart = firstIdx >= 0 ? hoursLabels[firstIdx] : startIso;
    const durationEnd = lastIdx >= 0 ? hoursLabels[lastIdx] : endIso;

    const durationText = durationStart && durationEnd
      ? `${formatTooltipTitle(durationStart, interval, true)} – ${formatTooltipTitle(durationEnd, interval, true)}`
      : '—';

    return { totalHours, remainingBudget, durationText };
  }, [hoursData, invoicedAmountData, interval, startIso, endIso, totalBudget]);

  const emptyState = useMemo(() => {
    const hasPrimary = (primaryChartData?.datasets?.[0]?.data || []).some((v) => Number(v) !== 0);
    const hasRevenue = (revenueData?.datasets?.[0]?.data || []).some((v) => Number(v) !== 0);
    const hasByUser = (activeByUserData?.datasets || []).some((ds) => (ds?.data || []).some((v) => Number(v) !== 0));
    return !(hasPrimary || hasRevenue || hasByUser);
  }, [primaryChartData, revenueData, activeByUserData]);

  const downloadMenuNode = (onPng, onCsv, onXlsx) => (
    <Dropdown
      trigger="click"
      placement="bottomEnd"
      noCaret
      size="xs"
      appearance="ghost"
      className="ppc-chart-download-menu"
      aria-label="Download"
      title={<i className="bi bi-download" aria-hidden="true" />}
    >
      <Dropdown.Item onClick={onPng}>Download PNG</Dropdown.Item>
      <Dropdown.Item onClick={onCsv}>Download CSV</Dropdown.Item>
      <Dropdown.Item onClick={() => void onXlsx?.()}>Download XLSX</Dropdown.Item>
    </Dropdown>
  );

  const headerNode = (title, onPng, onCsv, onXlsx, milestoneToggle = null) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" spacing={10} alignItems="center" wrap>
        <div style={{ fontWeight: 600 }}>{title}</div>
      </Stack>
      <Stack direction="row" spacing={10} alignItems="center" wrap>
        {milestoneToggle ? (
          <Checkbox checked={Boolean(milestoneToggle?.checked)} onChange={(_value, checked) => milestoneToggle?.onChange?.(Boolean(checked))}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Show milestones</span>
          </Checkbox>
        ) : null}
        {downloadMenuNode(onPng, onCsv, onXlsx)}
      </Stack>
    </Stack>
  );

  const primaryHeaderNode = (onPng, onCsv, onXlsx) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" wrap>
      <Stack direction="row" spacing={10} alignItems="center" wrap>
        <div style={{ fontWeight: 600 }}>{hoursChartMetric === 'dollars' ? 'Forecasted Dollars' : 'Forecasted Hours'}</div>
        <ButtonGroup size="xs">
          <Button
            appearance={hoursChartMetric === 'hours' ? 'primary' : 'ghost'}
            onClick={() => setHoursChartMetric('hours')}
          >
            Hours
          </Button>
          <Button
            appearance={hoursChartMetric === 'dollars' ? 'primary' : 'ghost'}
            onClick={() => setHoursChartMetric('dollars')}
          >
            Dollars
          </Button>
        </ButtonGroup>
      </Stack>

      <Stack direction="row" spacing={10} alignItems="center" wrap>
        <Checkbox checked={showMilestonesPrimary} onChange={(_value, checked) => setShowMilestonesPrimary(Boolean(checked))}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Show milestones</span>
        </Checkbox>
        {downloadMenuNode(onPng, onCsv, onXlsx)}
      </Stack>
    </Stack>
  );

  const byUserHeaderNode = (onPng, onCsv, onXlsx) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" wrap>
      <Stack direction="row" spacing={10} alignItems="center" wrap>
        <div style={{ fontWeight: 600 }}>{byUserMetric === 'utilization' ? 'Utilization by User' : 'Hours by User'}</div>
        <ButtonGroup size="xs">
          <Button
            appearance={byUserMetric === 'hours' ? 'primary' : 'ghost'}
            onClick={() => setByUserMetric('hours')}
          >
            Hours
          </Button>
          <Button
            appearance={byUserMetric === 'utilization' ? 'primary' : 'ghost'}
            onClick={() => setByUserMetric('utilization')}
          >
            % Util
          </Button>
        </ButtonGroup>

        <CheckPicker
          data={byUserSeriesOptions}
          value={Array.isArray(selectedByUserSeriesKeys) ? selectedByUserSeriesKeys : byUserSeriesValues}
          onChange={(value) => setSelectedByUserSeriesKeys(Array.isArray(value) ? value : [])}
          renderValue={renderByUserSelectionValue}
          cleanable={false}
          searchable
          placeholder="Filter people"
          style={{ width: 220, maxWidth: 220 }}
          disabled={byUserSeriesOptions.length === 0}
          renderExtraFooter={() => (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px' }}>
              <Button appearance="link" size="xs" onClick={selectAllByUserSeries} disabled={byUserSeriesOptions.length === 0}>
                Select all
              </Button>
              <Button appearance="link" size="xs" onClick={deselectAllByUserSeries} disabled={byUserSeriesOptions.length === 0}>
                Deselect all
              </Button>
            </div>
          )}
        />
      </Stack>

      <Stack direction="row" spacing={10} alignItems="center" wrap>
        <Checkbox checked={showMilestonesByUser} onChange={(_value, checked) => setShowMilestonesByUser(Boolean(checked))}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Show milestones</span>
        </Checkbox>
        {downloadMenuNode(onPng, onCsv, onXlsx)}
      </Stack>
    </Stack>
  );

  const exportPng = (chartRef, filename) => {
    const inst = chartRef?.current?.chart ?? chartRef?.current;
    const dataUrl = inst?.toBase64Image?.();
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const exportCsv = (chartData, filename) => {
    const csv = chartToCsv(chartData);
    downloadTextFile(filename, csv, 'text/csv;charset=utf-8');
  };

  const exportXlsx = async (chartData, filename, sheetName = 'Chart') => {
    const labels = Array.isArray(chartData?.labels) ? chartData.labels.map((l) => String(l ?? '')) : [];
    const datasets = Array.isArray(chartData?.datasets) ? chartData.datasets : [];

    const columns = [{ header: 'Period', key: 'period', width: 18 }];
    datasets.forEach((ds, idx) => {
      columns.push({
        header: String(ds?.label ?? `Series ${idx + 1}`),
        key: `s${idx}`,
        width: 18,
        numFmt: '0.00',
      });
    });

    const rows = labels.map((label, idx) => {
      const row = { period: label };
      datasets.forEach((ds, dsIdx) => {
        const value = Number(ds?.data?.[idx]);
        row[`s${dsIdx}`] = Number.isFinite(value) ? value : null;
      });
      return row;
    });

    await exportSheetsToXlsx({
      fileName: filename,
      sheets: [{ name: sheetName, columns, rows }],
    });
  };

  return (
    <div className="ppc-page">
      <div className="ppc-forecast-header">
        <div className="ppc-forecast-header-left">
          <h3 style={{ margin: 0, lineHeight: '22px' }}>Charts</h3>
        </div>
      </div>

      <Divider />

      {hasError ? (
        <Message type="error" showIcon>
          Failed to load charts. Try adjusting filters or retry.
        </Message>
      ) : null}

      <Panel bordered className="ppc-charts-top-panel">
        <div className="ppc-charts-controls" role="group" aria-label="Chart filters">
          <div className="ppc-charts-control ppc-charts-control--project">
            <div className="ppc-charts-filter-label" style={{ color: theme.textMuted }}>Project</div>
            <SelectPicker
              className="ppc-charts-project-picker"
              data={projectOptions}
              value={Number.isInteger(projectId) ? projectId : null}
              placeholder={projectsLoading ? 'Loading…' : 'All projects'}
              disabled={projectsLoading || isLoading}
              cleanable
              searchable
              menuAutoWidth={false}
              style={{ width: '100%', maxWidth: '100%' }}
              onChange={(v) => {
                if (v === null || v === undefined || v === '') {
                  allowAllProjectsRef.current = true;
                  setProjectId(null);
                  const r = makeThisYearRange();
                  setPendingRange(r);
                  setCommittedRange(r);
                  setRangePreset('this-year');
                  return;
                }
                const id = Number(v);
                if (!Number.isInteger(id)) return;

                allowAllProjectsRef.current = false;
                setProjectId(id);
                const selected = (projects || []).find((p) => Number(p?.project_id ?? p?.id) === id);
                const r = makeProjectDurationRange(selected) ?? allProjectsDurationRange ?? makeThisYearRange();
                setPendingRange(r);
                setCommittedRange(r);
                setRangePreset('project-duration');
              }}
            />
          </div>

          <div className="ppc-charts-control ppc-charts-control--interval">
            <div className="ppc-charts-filter-label" style={{ color: theme.textMuted }}>Interval</div>
            <SelectPicker
              block
              data={intervalOptions}
              value={interval}
              cleanable={false}
              searchable={false}
              menuAutoWidth={false}
              menuStyle={{ width: 150, minWidth: 150 }}
              disabled={isLoading}
              onChange={(v) => setIntervalKey(resolveInterval(v))}
            />
          </div>

          <div className="ppc-charts-control ppc-charts-control--preset">
            <div className="ppc-charts-filter-label" style={{ color: theme.textMuted }}>Date range</div>
            <SelectPicker
              block
              data={rangePresetOptions}
              value={rangePreset}
              cleanable={false}
              searchable={false}
              menuAutoWidth={false}
              menuStyle={{ width: 230, minWidth: 230 }}
              disabled={isLoading}
              onChange={(next) => {
                const preset = String(next || 'custom');
                setRangePreset(preset);

                if (preset === 'custom') return;
                if (preset === 'this-quarter') {
                  const r = makeThisQuarterRange();
                  setPendingRange(r);
                  setCommittedRange(r);
                  return;
                }
                if (preset === 'this-year') {
                  const r = makeThisYearRange();
                  setPendingRange(r);
                  setCommittedRange(r);
                  return;
                }
                if (preset === 'last-year') {
                  const r = makeLastYearRange();
                  setPendingRange(r);
                  setCommittedRange(r);
                  return;
                }
                if (preset === 'project-duration') {
                  const r = selectedProjectDurationRange ?? allProjectsDurationRange ?? makeThisYearRange();
                  setPendingRange(r);
                  setCommittedRange(r);
                }
              }}
            />
          </div>

          <div className="ppc-charts-control ppc-charts-control--dates">
            <div className="ppc-charts-filter-label" style={{ color: theme.textMuted }}>Dates</div>
            <DateRangePicker
              className="ppc-charts-date-picker"
              value={pendingRange}
              placement="bottomEnd"
              cleanable={false}
              showOneCalendar
              format="MM/dd/yyyy"
              ranges={makeYearRanges()}
              disabled={isLoading || rangePreset !== 'custom'}
              onChange={(next) => {
                if (!next || next.length !== 2) return;
                const a = next[0] instanceof Date ? next[0] : null;
                const b = next[1] instanceof Date ? next[1] : null;
                if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return;
                setPendingRange(a.getTime() <= b.getTime() ? [a, b] : [b, a]);
              }}
            />
          </div>
        </div>

        {isLoading ? (
          <Stack spacing={8} alignItems="center" style={{ padding: '8px 0' }}>
            <Loader content="Loading charts…" />
          </Stack>
        ) : null}

        {!isLoading && utilError ? (
          <Message type="warning" showIcon>
            Utilization chart may be incomplete (failed to load close dates).
          </Message>
        ) : null}

        {!isLoading && fteError ? (
          <Message type="warning" showIcon>
            Utilization chart is using 0% reserved hours (failed to load users).
          </Message>
        ) : null}

        {!isLoading && !hasError && emptyState ? (
          <Message type="info" showIcon>
            No forecast data for this project in the selected range. Add activities and forecasts to get started.
          </Message>
        ) : null}

        <div className="ppc-charts-grid">
          <Panel
            bordered
            className={`ppc-chart-panel${isAllProjects ? ' ppc-chart-panel--full' : ''}`}
            header={primaryHeaderNode(
              () =>
                exportPng(
                  hoursChartRef,
                  `${hoursChartMetric === 'dollars' ? 'forecasted-dollars' : 'forecasted-hours'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.png`
                ),
              () =>
                exportCsv(
                  primaryChartData,
                  `${hoursChartMetric === 'dollars' ? 'forecasted-dollars' : 'forecasted-hours'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.csv`
                ),
              () =>
                exportXlsx(
                  primaryChartData,
                  `${hoursChartMetric === 'dollars' ? 'forecasted-dollars' : 'forecasted-hours'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.xlsx`,
                  hoursChartMetric === 'dollars' ? 'Forecasted Dollars' : 'Forecasted Hours'
                )
            )}
          >
            <div className="ppc-chart-canvas" aria-label={hoursChartMetric === 'dollars' ? 'Forecasted dollars chart' : 'Forecasted hours chart'}>
              <Line ref={hoursChartRef} data={primaryChartData} options={hoursChartMetric === 'dollars' ? dollarsOptions : hoursOptions} />
            </div>
          </Panel>

          {!isAllProjects ? (
            <Panel
              bordered
              className="ppc-chart-panel"
              header={headerNode(
                'Budget Burndown',
                () =>
                  exportPng(
                    burndownChartRef,
                    `budget-burndown-${Number.isInteger(projectId) ? projectId : 'all'}-${interval}.png`
                  ),
                () => exportCsv(burndownData, `budget-burndown-${Number.isInteger(projectId) ? projectId : 'all'}-${interval}.csv`),
                () => exportXlsx(burndownData, `budget-burndown-${Number.isInteger(projectId) ? projectId : 'all'}-${interval}.xlsx`, 'Budget Burndown'),
                { checked: showMilestonesBurndown, onChange: setShowMilestonesBurndown }
              )}
            >
              <div className="ppc-chart-canvas" aria-label="Budget burndown chart">
                <Line ref={burndownChartRef} data={burndownData} options={burndownOptions} />
              </div>
            </Panel>
          ) : null}

          <Panel
            bordered
            className="ppc-chart-panel ppc-chart-panel--full"
            header={byUserHeaderNode(
              () =>
                exportPng(
                  byUserChartRef,
                  `${byUserMetric === 'utilization' ? 'utilization-by-user' : 'hours-by-user'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.png`
                ),
              () =>
                exportCsv(
                  activeByUserData,
                  `${byUserMetric === 'utilization' ? 'utilization-by-user' : 'hours-by-user'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.csv`
                ),
              () =>
                exportXlsx(
                  activeByUserData,
                  `${byUserMetric === 'utilization' ? 'utilization-by-user' : 'hours-by-user'}-${
                    Number.isInteger(projectId) ? projectId : 'all'
                  }-${interval}.xlsx`,
                  byUserMetric === 'utilization' ? 'Utilization by User' : 'Hours by User'
                )
            )}
          >
            {byUserMetric === 'utilization' ? (
              <div
                style={{
                  marginBottom: 8,
                  padding: '6px 8px',
                  borderRadius: 6,
                  background: '#f7f7f8',
                  fontSize: 12,
                  color: theme.textMuted,
                }}
              >
                The target utilization is 100%, these numbers include hours reduction and hours reserved for admin tasks
              </div>
            ) : null}
            <div className="ppc-chart-canvas" aria-label="Hours by user chart">
              <Line ref={byUserChartRef} data={activeByUserDataWithVisibility} options={byUserOptions} />
            </div>
          </Panel>

          {!isAllProjects ? (
            <Panel bordered className="ppc-chart-panel ppc-chart-panel--full" header="Metrics">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                <Panel bordered>
                  <div style={{ fontSize: 12, color: theme.textMuted, fontWeight: 700, marginBottom: 4 }}>
                    Total budget
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, lineHeight: '32px', color: theme.text }}>
                    {formatCurrency(totalBudget)}
                  </div>
                </Panel>

                <Panel bordered>
                  <div style={{ fontSize: 12, color: theme.textMuted, fontWeight: 700, marginBottom: 4 }}>
                    Remaining budget
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, lineHeight: '32px', color: theme.text }}>
                    {formatCurrency(totalsAndDuration.remainingBudget)}
                  </div>
                </Panel>

                <Panel bordered>
                  <div style={{ fontSize: 12, color: theme.textMuted, fontWeight: 700, marginBottom: 4 }}>
                    Total hours
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, lineHeight: '32px', color: theme.text }}>
                    {formatHours(totalsAndDuration.totalHours)}
                  </div>
                </Panel>

                <Panel bordered>
                  <div style={{ fontSize: 12, color: theme.textMuted, fontWeight: 700, marginBottom: 4 }}>
                    Project duration
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                    {totalsAndDuration.durationText}
                  </div>
                </Panel>
              </div>
            </Panel>
          ) : null}

        </div>
      </Panel>
    </div>
  );
}